if (!customElements.get('variant-multipicker')) {
  class VariantMultipicker extends HTMLElement {
    constructor() {
      super();

      this.quantityInputs = Array.from(this.querySelectorAll('.js-variant-multipicker-quantity'));
      this.addButton = this.querySelector('.js-variant-multipicker-add');
      this.errorMsg = this.querySelector('.js-form-error');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.modal = this.querySelector('modal-dialog');

      this.quantityInputs.forEach((input) => {
        input.addEventListener('input', this.updateAddButton.bind(this));
        input.addEventListener('change', this.handleQuantityChange.bind(this));
      });

      if (this.addButton) {
        this.addButton.addEventListener('click', this.handleSubmit.bind(this));
      }
    }

    /**
     * Handles quantity input changes.
     * @param {object} evt - Event object.
     */
    handleQuantityChange(evt) {
      const input = evt.currentTarget;
      const quantity = VariantMultipicker.getQuantity(input);

      input.value = quantity > 0 ? quantity : '';
      this.syncQuantityInput(input);
      this.setErrorMsgState();
      this.updateAddButton();
    }

    /**
     * Adds entered variant quantities to the cart.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      evt.preventDefault();

      const items = this.getItems();

      if (!this.addButton || this.addButton.getAttribute('aria-disabled') === 'true') {
        if (items.length === 0) {
          this.setErrorMsgState(this.dataset.emptyError);
        }
        return;
      }

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.setErrorMsgState();
      this.setLoadingState(true);

      let sections = 'cart-icon-bubble';
      let addSucceeded = false;

      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();
        const error = typeof data.description === 'string' ? data.description : data.message;

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          this.closeModal();
          VariantMultipicker.updateCartIcon(data);

          setTimeout(() => {
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();

        this.dispatchCartEvents(items, oldCartData, newCartData);
        this.clearQuantities();
        addSucceeded = true;
      } catch (error) {
        console.log(error); // eslint-disable-line
        if (!this.errorMsg || !this.errorMsg.textContent) {
          this.setErrorMsgState(this.dataset.cartError);
        }
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg ? this.errorMsg.textContent : this.dataset.cartError
          }
        }));
      } finally {
        this.setLoadingState(false, addSucceeded);
      }
    }

    /**
     * Returns cart items for quantity rows with a quantity greater than 0.
     * @returns {Array}
     */
    getItems() {
      return this.quantityInputs
        .filter((input) => !input.disabled)
        .map((input) => ({
          id: Number(input.dataset.variantId),
          quantity: VariantMultipicker.getQuantity(input)
        }))
        .filter((item) => item.id && item.quantity > 0);
    }

    /**
     * Gets a non-negative integer quantity from an input.
     * @param {HTMLInputElement} input - Quantity input.
     * @returns {number}
     */
    static getQuantity(input) {
      const quantity = Number.parseInt(input.value, 10);
      return Number.isNaN(quantity) || quantity < 0 ? 0 : quantity;
    }

    /**
     * Dispatches cart events for each added variant.
     * @param {Array} items - Added cart items.
     * @param {object} oldCartData - Cart data before the add request.
     * @param {object} newCartData - Cart data after the add request.
     */
    dispatchCartEvents(items, oldCartData, newCartData) {
      items.forEach((item) => {
        const numericVariantId = Number(item.id);
        const oldItem = oldCartData.items.find((item) => item.variant_id === numericVariantId);
        const newItem = newCartData.items.find((item) => item.variant_id === numericVariantId);

        if (oldItem && newItem) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: numericVariantId,
              oldQuantity: oldItem.quantity,
              newQuantity: newItem.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: numericVariantId
            }
          }));
        }
      });
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && response.sections && response.sections['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Updates the add button availability based on entered quantities.
     */
    updateAddButton() {
      if (!this.addButton) return;

      const hasQuantities = this.getItems().length > 0;
      this.addButton.disabled = !hasQuantities;
      this.addButton.setAttribute('aria-disabled', (!hasQuantities).toString());
    }

    /**
     * Clears all quantity inputs.
     */
    clearQuantities() {
      this.quantityInputs.forEach((input) => {
        input.value = '';
        this.syncQuantityInput(input);
      });
      this.updateAddButton();
    }

    /**
     * Keeps the quantity input custom element in sync after manual value changes.
     * @param {HTMLInputElement} input - Quantity input.
     */
    syncQuantityInput(input) {
      const quantityInput = input.closest('quantity-input');
      if (quantityInput) quantityInput.currentQty = input.value;
    }

    /**
     * Closes the containing modal before opening/updating cart UI.
     */
    closeModal() {
      if (this.modal && typeof this.modal.close === 'function') {
        this.modal.close();
      }
    }

    /**
     * Sets loading state on the submit button.
     * @param {boolean} loading - Whether the button is loading.
     * @param {boolean} showSuccess - Whether to briefly show success state.
     */
    setLoadingState(loading, showSuccess = false) {
      if (!this.addButton) return;

      if (loading) {
        this.addButton.setAttribute('aria-disabled', 'true');
        this.addButton.classList.add('is-loading');
        return;
      }

      if (showSuccess) {
        this.addButton.classList.add('is-success');
      }

      this.updateAddButton();

      setTimeout(() => {
        this.addButton.classList.remove('is-loading');
        this.addButton.classList.remove('is-success');
      }, 1400);
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) this.errorMsg.textContent = error;
    }
  }

  customElements.define('variant-multipicker', VariantMultipicker);
}
